import lodash from 'lodash'
import moment from 'moment'
import React, { useEffect } from 'react'
import styled from 'styled-components'

import Badge from '../../../../../../../../../../1stlog-ui/src/components/Badge'
import { useField } from '../../../../../../../../../../1stlog-ui/src/components/Field'

const Wrapper = styled.div`
  font-size: 1.1em;
`

const validate = (value) => (value ? value : undefined)

const TimeValidation = (props) => {
  const { timing = {}, allTimings, possibleTimings } = props

  const from = useField('from')
  const until = useField('until')
  const untilAlwaysValid = useField('untilAlwaysValid')

  const [requiresConfirmation, setRequiresConfirmation] = useField(
    'requiresConfirmation',
    {
      asState: true,
    },
  )

  const {
    input: { value: timeValidation, onChange: setTimeValidation },
    meta: { error },
  } = useField('timeValidation', {
    raw: true,
    validate,
    subscribeAll: true,
  })

  useEffect(() => {
    const realFrom = moment(from)
    const realUntil = moment(until) // next day if <=5:00 (TODO: next day if until < from?)
    // until should be next day
    if (moment(realUntil).isSameOrBefore(realFrom)) {
      realUntil.add(1, 'day')
    }
    const err = validateTiming({ from, until, timing, untilAlwaysValid })
    // console.log('err', err)

    setTimeout(() => {
      setRequiresConfirmation(lodash.get(timing, 'requiresConfirmation'))
      setTimeValidation(err)
    }, 0)
  }, [from, until, timing, untilAlwaysValid])

  if (!error) {
    return null
  }

  return (
    <Wrapper className="mt-0 mb-1">
      <Badge danger>
        <i className="fas fa-exclamation-triangle" /> <span>{error}</span>
      </Badge>
    </Wrapper>
  )
}

export default TimeValidation

function validateTiming({ from, until, timing, untilAlwaysValid }) {
  const diffInMinutes = moment
    .duration(moment(until).diff(moment(from)))
    .asMinutes()

  const {
    minAvisFromNow,
    minimalTimespan,
    from: min,
    until: max,
    mode,
  } = timing

  const now = moment()

  const isAllowedMidnight =
    !timing.startIsSelected &&
    moment(from).hour() === 0 &&
    moment(from).minute() === 0

  // if (
  //   ['timespan', 'lefttimespan'].includes(mode) &&
  //   moment(from).isBefore(now) &&
  //   !isAllowedMidnight
  // ) {
  //   return i18n.t(
  //     'start_time_cant_be_in_past',
  //     'Die Startzeit darf nicht in der Vergangenheit liegen.',
  //   )
  // }

  if (diffInMinutes < 0) {
    return i18n.t(
      'start_time_must_be_before_end_time',
      'Die Startzeit muss vor der Endzeit liegen.',
    )
  }

  if (minimalTimespan && minimalTimespan > Math.abs(diffInMinutes)) {
    return i18n.t('timespan_must_be_at_least_x_minutes', {
      defaultValue:
        'Der Zeitraum muss mindestens {{minimalTimespan}} Minuten betragen.',
      minimalTimespan,
    })
  }
  if (
    minAvisFromNow &&
    moment(from).subtract(minAvisFromNow, 'minutes').isBefore(moment())
  ) {
    if (timing.type === 'pickup') {
      return i18n.t('earliest_pickup_time_is_x', {
        defaultValue: 'Früheste Abholzeit um {{minAvisFromNow}} Uhr',
        minAvisFromNow: moment().add(minAvisFromNow, 'minutes').format('HH:mm'),
      })
    } else {
      return i18n.t('earliest_delivery_time_is_x', {
        defaultValue: 'Früheste Zustellzeit um {{minAvisFromNow}} Uhr',
        minAvisFromNow: moment().add(minAvisFromNow, 'minutes').format('HH:mm'),
      })
    }
  }

  // End time has to be at least minimalTimespan after start time
  if (
    (mode === 'righttimespan' || mode === 'timespan') &&
    moment(until).isBefore(moment(min).add(minimalTimespan, 'minutes'))
  ) {
    if (timing.type === 'pickup') {
      return i18n.t('end_of_pickup_timerange_at_x', {
        defaultValue: 'Ende des Abholzeitraums frühestens um {{time}} Uhr',
        time: moment(min).add(minimalTimespan, 'minutes').format('HH:mm'),
      })
    } else {
      return i18n.t('end_of_delivery_timerange_at_x', {
        defaultValue: 'Ende des Zustellzeitraums frühestens um {{time}} Uhr',
        time: moment(min).add(minimalTimespan, 'minutes').format('HH:mm'),
      })
    }
  }

  if (
    (moment(min).isAfter(moment(from)) && !isAllowedMidnight) ||
    (moment(max).isBefore(moment(until)) && !untilAlwaysValid)
  ) {
    const minText = timing.startIsSelected
      ? moment(min).format('HH:mm')
      : i18n.t('startof_day_or_value', {
          defaultValue: 'Tagesbeginn oder {{value}}',
          value: moment(min).format('HH:mm'),
        })
    const maxText = moment(max).format('HH:mm')
    if (timing.type === 'pickup') {
      return i18n.t('pickup_time_between_x_and_y', {
        defaultValue: 'Abholung nur zwischen {{min}} und {{max}} erlaubt.',
        min: minText,
        max: maxText,
      })
    } else {
      return i18n.t('delivery_time_between_x_and_y', {
        defaultValue: 'Zustellung nur zwischen {{min}} und {{max}} erlaubt.',
        min: minText,
        max: maxText,
      })
    }
  }

  if (
    (mode === 'lefttimespan' || mode === 'timespan') &&
    moment(from).isBefore(moment()) &&
    !isAllowedMidnight
  ) {
    return timing.type === 'pickup'
      ? i18n.t(
          'pickup_time_must_be_in_future',
          'Abholzeit muss in der Zukunft liegen.',
        )
      : i18n.t(
          'delivery_time_must_be_in_future',
          'Zustellzeit muss in der Zukunft liegen.',
        )
  }

  if (
    (mode === 'righttimespan' || mode === 'timespan') &&
    moment(until).isBefore(moment())
  ) {
    return timing.type === 'pickup'
      ? i18n.t(
          'end_of_pickup_time_must_be_in_future',
          'Ende des Abholzeitraums muss in der Zukunft liegen.',
        )
      : i18n.t(
          'end_of_delivery_time_must_be_in_future',
          'Ende des Zustellzeitraums muss in der Zukunft liegen.',
        )
  }

  return undefined
}
